import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, ChevronRight, Receipt } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@tabipla/ui";
import type { GroupResponseDto } from "@tabipla/shared";
import { EXPENSE_CATEGORIES, getCategoryClasses } from "../../config/categories";

interface RecentExpense {
  id: string;
  groupId: string;
  sessionId: string;
  description: string;
  amount: number;
  category: string;
  paidById: string;
  paidByName: string;
  createdAt: string;
}

interface RecentExpensesPreviewProps {
  expenses: RecentExpense[];
  groups: GroupResponseDto[];
  limit?: number;
}

export function RecentExpensesPreview({ expenses, groups, limit = 3 }: RecentExpensesPreviewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return t("activity.today", "今日");
    } else if (diffDays === 1) {
      return t("activity.yesterday", "昨日");
    } else if (diffDays < 7) {
      return t("activity.daysAgo", "{{days}}日前", { days: diffDays });
    } else {
      return date.toLocaleDateString("ja-JP", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const displayExpenses = expenses.slice(0, limit);

  if (displayExpenses.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg">{t("home.recentExpenses", "最近の出費")}</CardTitle>
        <button
          onClick={() => navigate("/activity")}
          className="flex items-center gap-1 text-sm text-emerald-600 transition-colors hover:text-emerald-700 dark:text-emerald-400"
        >
          {t("home.viewAll", "すべて見る")}
          <ChevronRight className="h-4 w-4" />
        </button>
      </CardHeader>
      <CardContent className="space-y-2">
        {displayExpenses.map((expense) => {
          const group = groups.find((g) => g.id === expense.groupId);
          const category =
            EXPENSE_CATEGORIES[expense.category as keyof typeof EXPENSE_CATEGORIES] ||
            EXPENSE_CATEGORIES.other;
          const IconComponent = category.icon;

          return (
            <div
              key={expense.id}
              className="flex cursor-pointer items-center gap-3 rounded-lg border bg-card p-3 transition-all hover:shadow-sm active:scale-[0.99]"
              onClick={() => navigate(`/groups/${expense.groupId}`)}
            >
              {/* Category Icon */}
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${getCategoryClasses(expense.category as keyof typeof EXPENSE_CATEGORIES)}`}
              >
                <IconComponent className="h-5 w-5" />
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="truncate text-sm font-medium">{expense.description}</h3>
                  <span className="shrink-0 font-bold tabular-nums">
                    {formatAmount(expense.amount)}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                  {group && <span className="truncate">{group.name}</span>}
                  <span className="flex shrink-0 items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDate(expense.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Show more link if there are more expenses */}
        {expenses.length > limit && (
          <button
            onClick={() => navigate("/activity")}
            className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed p-3 text-sm text-muted-foreground transition-colors hover:border-emerald-300 hover:text-emerald-600"
          >
            <Receipt className="h-4 w-4" />
            {t("home.moreExpenses", "他{{count}}件の出費を見る", {
              count: expenses.length - limit,
            })}
          </button>
        )}
      </CardContent>
    </Card>
  );
}
